/* eslint-disable max-lines */
"use client";

import React, { useEffect, useRef, useState } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";

import ScrollReveal, {
  ScrollRevealContent,
} from "@/components/common/ScienceScrollReveal/ScienceScrollReveal";
import ScienceSectionIntro from "@/components/common/ScienceSectionIntro/ScienceSectionIntro";
import ScienceSlider, {
  ScienceIndicator,
  ScienceSlideContent,
  ScienceSliderButton,
  type ScienceSliderApi,
} from "@/components/common/ScienceSlider/ScienceSlider";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import type {
  ScienceLifeStageCause,
  ScienceLifeStageData,
  ScienceLifeStageProduct,
  ScienceResponsiveImage,
} from "@/data/hair/brand/learn/science/science.types";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useNavigation } from "@/hooks/useNavigation";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

const TRANSITION_SPEED = 600;
const WAVE_HEIGHT = 320;
const waves = [1, 20, 40, 60];

function ResponsiveImage({
  src,
  alt,
  className,
  loading = "lazy",
}: {
  src?: ScienceResponsiveImage;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
}) {
  if (!src?.mobile && !src?.desktop) return null;
  const fallback = src.mobile || src.desktop || "";
  return (
    <div className="h-full">
      <Picture
        src={fallback}
        sources={
          src.mobile && src.desktop ? [{ media: "(min-width: 768px)", srcSet: src.desktop }] : []
        }
        alt={alt}
        loading={loading}
        className={className}
      />
    </div>
  );
}

function ProductTags({
  products,
  active,
  onClick,
}: {
  products: ScienceLifeStageProduct[];
  active: number;
  onClick: (index: number) => void;
}) {
  if (!products?.length) return null;

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 px-4">
      {products.map((product, index) => (
        <button
          type="button"
          key={index}
          aria-label={product.name}
          onClick={() => onClick(index)}
          className={cn(
            "transition-all duration-500 ease-in-out flex items-center justify-center border rounded max-h-[26px] px-2 py-1",
            active === index
              ? "bg-master-base-default border-master-base-default/20"
              : "text-master-base-default border-master-base-default/20"
          )}
        >
          <TextElementNova
            element="span"
            className={cn(
              "CTA-20-Dia-Normal xl:p-4",
              active === index ? "" : "text-nova-base-white"
            )}
            text={product.name}
          />
        </button>
      ))}
    </div>
  );
}

const getShape = (y: number, factor: number) => {
  const computedY = WAVE_HEIGHT + y - Math.abs(factor) * (WAVE_HEIGHT - 1);
  return `M0 ${WAVE_HEIGHT}C106 ${WAVE_HEIGHT} 89 ${computedY} 150 ${computedY}C211 ${computedY} 193 ${WAVE_HEIGHT} 300 ${WAVE_HEIGHT}`;
};

function getFactor(value: number, values: { value: number }[]) {
  const max = Math.max(...values.map((v) => v.value));
  return value / max;
}

function Wave({ y, index, factor }: { y: number; index: number; factor: number }) {
  const waveRef = useRef<SVGPathElement>(null);

  useGSAP(() => {
    if (!waveRef.current) return;
    gsap.set(waveRef.current, { attr: { d: getShape(y, factor) } });
  }, []);

  useGSAP(() => {
    if (!waveRef.current) return;
    gsap.to(waveRef.current, {
      duration: 0.4,
      attr: { d: getShape(y, factor) },
      ease: "power2.out",
    });
  }, [factor]);

  return (
    <path
      shapeRendering="geometricPrecision"
      strokeLinejoin="round"
      ref={waveRef}
      stroke="#fff"
      strokeWidth="1"
      opacity={1 - index * 0.25}
      fill="none"
    />
  );
}

function Waves({
  factor,
  name,
  active,
  isIntroAnimation,
}: {
  factor: number;
  name: string;
  active?: string;
  isIntroAnimation?: boolean;
}) {
  const nameRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!nameRef.current) return;
    gsap.set(nameRef.current, {
      yPercent: -factor * 100,
      duration: 0.4,
      ease: "power2.out",
    });
  });

  useGSAP(() => {
    if (!nameRef.current) return;
    gsap.to(nameRef.current, {
      yPercent: -factor * 100,
      duration: 0.4,
      ease: "power2.out",
    });
  }, [factor]);

  return (
    <div
      className={cn(
        "relative w-full h-full flex-shrink-0",
        `wave-${name}`,
        active === name ? "wave-active lg:opacity-100" : "lg:opacity-35"
      )}
    >
      <div
        ref={nameRef}
        data-hide={isIntroAnimation}
        className={cn(
          "transition-opacity duration-300 absolute bottom-0 w-full h-full flex items-end justify-center pointer-events-none text-center mb-2 lg:mb-4",
          isIntroAnimation && "opacity-0"
        )}
      >
        <TextElementNova text={name} className="Body-30-Dia-Normal text-master-base-default" />
      </div>
      <svg
        className="w-full h-full"
        style={{ transform: "translate3D(0,0,0)" }}
        width="300"
        height={`${WAVE_HEIGHT + 1}`}
        viewBox={`0 0 300 ${WAVE_HEIGHT + 1}`}
        fill="none"
      >
        {waves.map((y, i) => (
          <Wave key={i} y={y} factor={factor} index={i} />
        ))}
      </svg>
    </div>
  );
}

const ArrowIcon = ({ className }: { className?: string }) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect width="32" height="32" rx="2" fill="white" fillOpacity="0.2" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.5385 10.1948L17.0671 9.72339L16.1242 10.6662L16.5957 11.1376L20.7903 15.3322H9.60026H8.93359V16.6655H9.60026H20.7916L16.5957 20.8615L16.1242 21.3329L17.0671 22.2757L17.5385 21.8043L22.8718 16.4709C23.1321 16.2106 23.1321 15.7885 22.8718 15.5281L17.5385 10.1948Z"
      fill="white"
    />
  </svg>
);

function WavesDesktop({
  values,
  active,
  onMove,
  isIntroAnimation,
}: {
  values: ScienceLifeStageCause[];
  active: string;
  onMove: (index: number) => void;
  isIntroAnimation?: boolean;
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const idx = values.findIndex((v) => v.name === active);
    if (idx >= 0) setSelectedIndex(idx);
  }, [active, values]);

  const goLeft = () => {
    let newIndex = selectedIndex - 1;
    if (newIndex < 0) newIndex = values.length - 1;
    setSelectedIndex(newIndex);
    onMove(newIndex);
  };

  const goRight = () => {
    let newIndex = selectedIndex + 1;
    if (newIndex > values.length - 1) newIndex = 0;
    setSelectedIndex(newIndex);
    onMove(newIndex);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={goLeft}
        className="absolute top-1/2 -translate-y-1/2 z-10 left-4 w-8 h-8"
        aria-label="Previous"
      >
        <ArrowIcon className="rotate-180" />
      </button>
      <button
        type="button"
        onClick={goRight}
        className="absolute top-1/2 -translate-y-1/2 z-10 right-4 w-8 h-8"
        aria-label="Next"
      >
        <ArrowIcon />
      </button>
      <ul className="flex flex-row px-10 list-none m-0 mx-auto">
        {values.map((v, i) => (
          <li key={i} className="w-1/6">
            <Waves
              isIntroAnimation={isIntroAnimation}
              factor={getFactor(v.value, values)}
              name={v.name}
              active={active}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function WavesSlider({
  values,
  active,
  onMove,
  isIntroAnimation,
}: {
  values: ScienceLifeStageCause[];
  active: string;
  onMove: (name: string) => void;
  isIntroAnimation?: boolean;
}) {
  const sliderRef = useRef<ScienceSliderApi>(null);

  useEffect(() => {
    const index = values.findIndex((v) => v.name === active);
    if (index >= 0) sliderRef.current?.go(index);
  }, [active, values]);

  return (
    <ScienceSlider
      ref={sliderRef}
      loop
      align="center"
      showArrows={false}
      overflow="visible"
      className="cursor-grab xl:cursor-auto"
      /*
       * Gatsby .splide__track { padding-top: 60px; padding-bottom: 200px }
       * — lifts waves above the absolute description at bottom.
       */
      viewportClassName="pt-[60px] pb-[200px]"
      slideClassName="flex-[0_0_33.33vw] lg:flex-[0_0_25vw] xl:flex-[0_0_20vw]"
      onMove={(index) => {
        const clamped = Math.max(Math.min(index, values.length - 1), 0);
        onMove(values[clamped].name);
      }}
    >
      {values.map((v, i) => (
        <Waves
          key={i}
          isIntroAnimation={isIntroAnimation}
          factor={getFactor(v.value, values)}
          name={v.name}
          active={active}
        />
      ))}
    </ScienceSlider>
  );
}

function LifeStageMobile({
  backgroundSequence,
  products,
  activeProduct,
  setActiveProduct,
  activeCause,
  setActiveCause,
}: ScienceLifeStageData & {
  activeProduct: number;
  setActiveProduct: (n: number) => void;
  activeCause: string;
  setActiveCause: (n: string) => void;
}) {
  const activeIndex =
    products?.[activeProduct]?.causes.findIndex((cause) => cause.name === activeCause) ?? 0;

  return (
    <ScrollReveal
      selector="lifestage-mobile"
      backgroundSequence={backgroundSequence}
      backgroundScaleInitial={1.2}
      backgroundScale={1.6}
      showContentTreshold={1}
      offset={250}
      backgroundColor="#482F27"
      animationConfig={{
        imageScale: true,
        backgroundColor: true,
        // Darken bg for white type/waves (container color alone sits under opaque images)
        blur: true,
        clipPath: false,
        overlay: true,
      }}
      content={({ intro }) => (
        <ScrollRevealContent
          intro={intro}
          className="relative h-full w-full py-8 flex flex-col justify-between text-master-base-default"
        >
          <ProductTags products={products} active={activeProduct} onClick={setActiveProduct} />

          <div className="relative" data-intro-element>
            <WavesSlider
              isIntroAnimation={intro}
              active={activeCause}
              onMove={setActiveCause}
              values={products?.[activeProduct]?.causes}
            />
          </div>

          <div className="absolute top-1/2 translate-y-1/2 w-full flex justify-between pointer-events-none px-6 pt-6">
            <ScienceSliderButton
              type="prev"
              disabled={activeIndex === 0}
              onClick={() => {
                setActiveCause(products?.[activeProduct]?.causes[activeIndex - 1].name);
                segmentEvent("Carousel Clicked", {
                  arrow: "left",
                  slide_name: products?.[activeProduct]?.causes[activeIndex - 1].name,
                  module_location: "mid_fold",
                  module_name: "Lifestage",
                  slide_number: activeIndex,
                });
              }}
            />
            <ScienceSliderButton
              type="next"
              disabled={activeIndex === products?.[activeProduct]?.causes.length - 1}
              onClick={() => {
                setActiveCause(products?.[activeProduct]?.causes[activeIndex + 1].name);
                segmentEvent("Carousel Clicked", {
                  arrow: "right",
                  slide_name: products?.[activeProduct]?.causes[activeIndex + 1].name,
                  module_location: "mid_fold",
                  module_name: "Lifestage",
                  slide_number: activeIndex + 2,
                });
              }}
            />
          </div>

          <div className="w-full h-[160px] px-6 pt-4 absolute bottom-0 pointer-events-none">
            <ScienceSlideContent
              delay={TRANSITION_SPEED - 200}
              active={activeIndex}
              contents={products?.[activeProduct]?.causes?.map((cause, index) => (
                <div key={index} className="flex items-center text-center">
                  <TextElementNova
                    className="Body-20-Dia-Normal text-nova-base-white"
                    text={cause.text}
                  />
                </div>
              ))}
            />
          </div>
          <div className="px-2 w-full flex justify-center bottom-[-25px] absolute">
            <ScienceIndicator
              length={products?.[activeProduct]?.causes.length}
              active={activeIndex}
            />
          </div>
        </ScrollRevealContent>
      )}
    />
  );
}

function LifeStageDesktop({
  image,
  products,
  activeProduct,
  setActiveProduct,
  activeCause,
  moveSlider,
}: ScienceLifeStageData & {
  activeProduct: number;
  setActiveProduct: (n: number) => void;
  activeCause: string;
  moveSlider: (index: number) => void;
}) {
  const activeIndex =
    products?.[activeProduct]?.causes.findIndex((cause) => cause.name === activeCause) ?? 0;

  return (
    <ScrollReveal
      className="relative w-full overflow-hidden h-full"
      selector="lifestage-desktop"
      background={
        <ResponsiveImage src={image?.src} alt={image?.alt || ""} className="object-cover" />
      }
      showContentTreshold={0.8}
      offset={200}
      animationConfig={{
        blur: true,
        clipPath: false,
        imageScale: true,
        overlay: true,
      }}
      backgroundColor="#111"
      content={({ intro }) => (
        <ScrollRevealContent
          intro={intro}
          className="relative h-full w-full flex flex-col justify-between gap-[60px] py-14"
        >
          <div className="relative z-20 pb-xl">
            <ProductTags products={products} active={activeProduct} onClick={setActiveProduct} />
          </div>

          <div className="relative py-10 my-auto" data-intro-element>
            <WavesDesktop
              isIntroAnimation={intro}
              active={activeCause}
              onMove={moveSlider}
              values={products[activeProduct].causes}
            />
          </div>

          <div className="w-full h-[120px] px-6 pb-6 text-center lg:max-w-[900px] mx-auto">
            <ScienceSlideContent
              delay={TRANSITION_SPEED - 200}
              active={activeIndex || 0}
              contents={products?.[activeProduct]?.causes?.map((cause, index) => (
                <div key={index} className="flex items-center text-center">
                  <TextElementNova
                    className="Body-30-Dia-Normal text-nova-base-white"
                    text={cause.text}
                  />
                </div>
              ))}
            />
          </div>
        </ScrollRevealContent>
      )}
    />
  );
}

export default function LifeStage(props: ScienceLifeStageData) {
  const { intro, id, additionalText, additionalTitle, cta, products } = props;
  const isMobile = useIsMobile(768);
  const navigate = useNavigation();
  const [activeProduct, _setActiveProduct] = useState(0);
  const [activeCause, _setActiveCause] = useState(products[0].causes[0].name);

  const setActiveProduct = (val: number) => {
    _setActiveProduct(val);
    const firstCause = products[val]?.causes[0]?.name;
    if (firstCause) _setActiveCause(firstCause);
    segmentEvent("Module Tab Clicked", {
      module_location: "mid_fold",
      text: products[val]?.name,
      type: "button",
    });
  };

  const setActiveCause = (val: string) => {
    _setActiveCause(val);
  };

  const moveSlider = (index: number) => {
    setActiveCause(products[activeProduct].causes[index].name);
  };

  const onCtaClick = () => {
    if (!cta?.button) return;
    const isQuiz = cta.button.link?.includes("quiz");
    segmentEvent("Marketing CTA Clicked", {
      module_location: "mid_fold",
      destination: isQuiz ? "/hair/brand/quiz/" : cta.button.link,
      text: cta.button.label,
      type: "button",
    });
    if (isQuiz) {
      setTimeout(() => {
        window.location.href = cta.button.link;
      }, 250);
    } else {
      navigate(cta.button.link);
    }
  };

  if (!products) return null;

  const commonProps = {
    ...props,
    activeProduct,
    setActiveProduct,
    activeCause,
    setActiveCause,
    moveSlider,
  };

  const renderCTABlock = Boolean(cta || additionalText);

  return (
    <section
      data-section
      id={id}
      className="relative z-[1] py-8 md:py-12 xl:py-20 bg-nova-base-white"
    >
      <div className="w-full max-w-[1440px] mx-auto md:px-8">
        {intro && <ScienceSectionIntro {...intro} />}

        {isMobile ? (
          <div className="md:hidden pb-10">
            <LifeStageMobile {...commonProps} />
          </div>
        ) : (
          <div className="hidden md:block pb-20">
            <LifeStageDesktop {...commonProps} />
          </div>
        )}

        {renderCTABlock && (
          <div className="w-full max-w-[1440px] mx-auto px-6 md:px-0 [&_.cta-banner-button]:!w-full">
            <div className="flex flex-col justify-around gap-y-10 px-6 py-10 lg:flex-row border-nova-transparency-black-20 border rounded-sm">
              <div className="h-full w-full xl:items-center lg:max-w-[553px]">
                <TextElementNova
                  element="h3"
                  className="Title-20-Super-Normal text-nova-base-black pb-4"
                  text={additionalTitle || ""}
                />
                <TextElementNova
                  element="p"
                  className="Body-30-Dia-Normal text-nova-base-black"
                  text={additionalText || ""}
                />
              </div>
              {cta ? (
                <div className="w-full lg:max-w-[260px]">
                  <div className="relative flex flex-col items-center justify-center gap-5 rounded-lg text-center bg-nova-base-white lg:h-full">
                    {cta.headline && (
                      <TextElementNova
                        element="h3"
                        text={cta.headline}
                        className="Title-10-Super-Normal text-nova-base-white"
                      />
                    )}
                    <ButtonNova
                      icon="Right"
                      className="cta-banner-button"
                      label={cta.button.label}
                      onClick={onCtaClick}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
